import { Box, Flex, Sans, Serif } from "@artsy/palette"
import React from "react"
import { RelayRefetchProp, createRefetchContainer, graphql } from "react-relay"

import { SystemContextProps, useSystemContext } from "v2/Artsy"
import { renderWithLoadProgress } from "v2/Artsy/Relay/renderWithLoadProgress"
import { SystemQueryRenderer as QueryRenderer } from "v2/Artsy/Relay/SystemQueryRenderer"

import { AppSecondFactorFragmentContainer as AppSecondFactor } from "./Components/AppSecondFactor"
import { BackupSecondFactorFragmentContainer as BackupSecondFactor } from "./Components/BackupSecondFactor"
import { SmsSecondFactorFragmentContainer as SmsSecondFactor } from "./Components/SmsSecondFactor"

import { TwoFactorAuthentication_me } from "v2/__generated__/TwoFactorAuthentication_me.graphql"
import { TwoFactorAuthenticationQuery } from "v2/__generated__/TwoFactorAuthenticationQuery.graphql"

export interface TwoFactorAuthenticationProps extends SystemContextProps {
  me: TwoFactorAuthentication_me
  relay: RelayRefetchProp
}

const TwoFactorAuthentication: React.FC<TwoFactorAuthenticationProps> = props => {
  const { me, relay } = props

  return (
    <Box>
      <Flex
        flexDirection={["column", "row"]}
        alignItems={["flex-start", "flex-end"]}
      >
        <Serif size="6" color="black100">
          Two-factor Authentication
        </Serif>
        {me.hasSecondFactorEnabled && (
          <Sans ml={[0, 1]} weight="medium" size="4" color="green100">
            Enabled
          </Sans>
        )}
      </Flex>
      <Serif mt={1} size="3t" maxWidth="515px" color="black60">
        Set up an additional layer of security by requiring a security code in
        addition to your password to log in to your Artsy account.
      </Serif>
      <AppSecondFactor mt={3} me={me} relayRefetch={relay} />
      <SmsSecondFactor mt={2} me={me} relayRefetch={relay} />
      {me.hasSecondFactorEnabled && <BackupSecondFactor mt={2} me={me} />}
    </Box>
  )
}

export const TwoFactorAuthenticationRefetchContainer = createRefetchContainer(
  TwoFactorAuthentication,
  {
    me: graphql`
      fragment TwoFactorAuthentication_me on Me {
        hasSecondFactorEnabled

        ...AppSecondFactor_me
        ...SmsSecondFactor_me
        ...BackupSecondFactor_me
      }
    `,
  },
  graphql`
    query TwoFactorAuthenticationRefetchQuery {
      me {
        ...TwoFactorAuthentication_me
      }
    }
  `
)

export const TwoFactorAuthenticationQueryRenderer = () => {
  const { user, relayEnvironment } = useSystemContext()

  if (!user) {
    return null
  }

  return (
    <QueryRenderer<TwoFactorAuthenticationQuery>
      environment={relayEnvironment}
      variables={{}}
      query={graphql`
        query TwoFactorAuthenticationQuery @raw_response_type {
          me {
            ...TwoFactorAuthentication_me
          }
        }
      `}
      render={renderWithLoadProgress(TwoFactorAuthenticationRefetchContainer)}
    />
  )
}
